import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Grid,
  Typography,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import useUpdate from "../../../../api/useUpdate";
import useDelete from "../../../../api/useDelete";
import { useAuth } from "../../../../context";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import { useTranslation } from "react-i18next";

const ProcedureType = () => {
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const [editMode, setEditMode] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const { t } = useTranslation();

  const {
    data: types,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], "procedure-type", token);

  const {
    handleAdd,
    loading: loadingAdd,
    resetForm,
  } = useAdd("procedure-type", token);
  const { handleUpdate, loading: loadingEdit } = useUpdate(
    "procedure-type",
    token
  );
  const { handleDelete, ConfirmDialog } = useDelete("procedure-type", token);

  useEffect(() => {
    if (resetForm) {
      setName("");
      setType("");
      setEditMode(false);
      setSelectedType(null);
    }
  }, [resetForm]);

  const handleEdit = (type) => {
    setEditMode(true);
    setSelectedType(type);
    setName(type.name);
    setType(type.type); // Set the type for editing
  };

  const handleSubmit = () => {
    if (editMode) {
      handleUpdate(selectedType.id, { name, type });
      setName("");
      setType("");
      setEditMode(false);
    } else {
      handleAdd({ name, type });
    }
  };

  const handleDeleteClick = (id) => {
    handleDelete(id);
  };

  if (isLoadingType)
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: "60vh" }}
      >
        <CircularProgress />
      </Grid>
    );
  if (isErrorType) return <ErrorPage />;

  return (
    <Grid container padding={3} justifyContent={"center"} mt={1}>
      <Grid
        item
        xs={12}
        sm={10}
        md={6}
        boxShadow={"0px 0px 5px 1px #f0eada"}
        borderRadius={"10px"}
        padding={"15px"}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            borderBottom: "5px solid #f0eada",
            pb: 1,
            textAlign: "center",
            fontWeight: "bold",
          }}
        >
          {editMode
            ? t("call_center.editProcedureType")
            : t("call_center.addProcedureType")}
        </Typography>
        <Grid container spacing={2} alignItems="center" mt={2}>
          <Grid item xs={6}>
            <TextField
              size="small"
              label={t("call_center.name")}
              type="text"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              size="small"
              label={t("call_center.type")}
              type="text"
              fullWidth
              value={type}
              onChange={(e) => setType(e.target.value)} // Update type state
            >
              <MenuItem value={1}>Ht</MenuItem>
              <MenuItem value={2}>Beauty</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loadingAdd || loadingEdit}
              fullWidth
            >
              {loadingAdd || loadingEdit ? (
                <CircularProgress size={24} />
              ) : editMode ? (
                t("call_center.edit")
              ) : (
                t("call_center.save")
              )}
            </Button>
          </Grid>
        </Grid>

        <TableContainer sx={{ maxHeight: 400, mt: 1 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f0eada" }}>
              <TableRow>
                <TableCell>{t("call_center.id")}</TableCell>
                <TableCell>{t("call_center.name")}</TableCell>
                <TableCell>{t("call_center.type")}</TableCell>{" "}
                {/* Added type column */}
                <TableCell>{t("call_center.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {types.map((type) => (
                <TableRow key={type.id} sx={{ height: 40 }}>
                  <TableCell>{type.id}</TableCell>
                  <TableCell>{type.name}</TableCell>
                  <TableCell>
                    {type.type === 1 && "Ht"}
                    {type.type === 2 && "Beauty"}
                  </TableCell>{" "}
                  {/* Display type */}
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(type)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => handleDeleteClick(type.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <ConfirmDialog />
    </Grid>
  );
};

export default ProcedureType;
